// src/pages/RapportPage.js

import { metLiveStijlen } from '../utils/doorloopjePdf';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { trackEvent } from '../utils/track';
import axios from 'axios';
import { videoHeaders, isVerificatieFout, meldVerificatieVerlopen } from '../utils/videoVerificatie';
import VideoFeedbackKaart from '../components/VideoFeedbackKaart';
import VideoPrivacyInfo from '../components/VideoPrivacyInfo';
import useVideoBewaartermijn from '../hooks/useVideoBewaartermijn';
// jsPDF and html2canvas are loaded dynamically in handleDownloadPDF() to reduce bundle size

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Helper: proxify image URLs to avoid CORS issues
const proxify = (url) =>
  `${process.env.REACT_APP_STRAPI_URL}/api/media-proxy?url=${encodeURIComponent(url)}`;

// Strip timestamp patterns (e.g. "1:23", "1:23:45") from text for PDF
function stripTimestamps(text) {
  if (!text) return text;
  return text.replace(/\s*\(?\d{1,2}:\d{2}(?::\d{2})?\)?\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

function timestampToSeconds(ts) {
  const segments = ts.split(':').map(Number);
  if (segments.length === 3) {
    return segments[0] * 3600 + segments[1] * 60 + segments[2];
  }
  return segments[0] * 60 + segments[1];
}

function formatTime(seconds) {
  const s = Math.floor(seconds);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

function toTitleCase(str) {
  if (!str) return str;
  return str.toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
}

function TimestampText({ text, videoRef }) {
  if (!text) return null;
  const pattern = /(\d{1,2}:\d{2}(?::\d{2})?)/g;
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, index) => {
        if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(part)) {
          return (
            <button
              key={index}
              className="rapport-timestamp"
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = timestampToSeconds(part);
                  videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  videoRef.current.play();
                }
              }}
            >
              {part}
            </button>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
}

const MARKER_COLORS = {
  sterk_punt: '#4CAF50',
  verbeterpunt: '#FF9800',
  notitie: '#AB47BC',
};
const MARKER_ICONS = {
  sterk_punt: '✓',
  verbeterpunt: '💡',
  notitie: '✎',
};

function TimelineBar({ markers, notes, duration, currentTime, onSeek }) {
  const noteMarkers = (notes || []).map(n => ({
    seconds: n.timestamp_seconds,
    label: formatTime(n.timestamp_seconds),
    step: null,
    stepTitle: 'Jouw notitie',
    type: 'notitie',
    context: n.text,
  }));
  // Filter out observations — only show sterk_punt, verbeterpunt, notitie
  const filteredMarkers = markers.filter(m => m.type !== 'observatie');
  const allMarkers = [...filteredMarkers, ...noteMarkers].sort((a, b) => a.seconds - b.seconds);
  const barRef = useRef(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverX, setHoverX] = useState(0);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const effectiveDuration = (duration && isFinite(duration) && duration > 0)
    ? duration
    : allMarkers.length > 0
      ? allMarkers[allMarkers.length - 1].seconds * 1.1
      : 0;

  if (!effectiveDuration || allMarkers.length === 0) return null;

  const handleBarClick = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const seconds = (x / rect.width) * effectiveDuration;
    onSeek(Math.max(0, Math.min(effectiveDuration, seconds)));
  };

  const handleBarHover = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const seconds = (x / rect.width) * effectiveDuration;
    setHoverTime(Math.max(0, Math.min(effectiveDuration, seconds)));
    setHoverX(x);
  };

  return (
    <div className="rapport-timeline">
      <div
        ref={barRef}
        className="rapport-timeline-bar"
        onClick={handleBarClick}
        onMouseMove={handleBarHover}
        onMouseLeave={() => { setHoverTime(null); setActiveTooltip(null); }}
      >
        <div
          className="rapport-timeline-progress"
          style={{ width: `${(currentTime / effectiveDuration) * 100}%` }}
        />
        {hoverTime !== null && (
          <div className="rapport-timeline-hover" style={{ left: `${hoverX}px` }}>
            {formatTime(hoverTime)}
          </div>
        )}
        {allMarkers.map((m, i) => (
          <button
            key={`${m.type}-${m.seconds}-${i}`}
            className={`rapport-timeline-dot rapport-timeline-dot--${m.type}`}
            style={{
              left: `${(m.seconds / effectiveDuration) * 100}%`,
              background: MARKER_COLORS[m.type] || 'var(--color-yellow)',
            }}
            onClick={(e) => { e.stopPropagation(); onSeek(m.seconds); }}
            onMouseEnter={() => setActiveTooltip(`${m.type}-${m.seconds}-${i}`)}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <span className="rapport-timeline-icon">{MARKER_ICONS[m.type] || '●'}</span>
          </button>
        ))}
        {allMarkers.map((m, i) => {
          const key = `${m.type}-${m.seconds}-${i}`;
          if (activeTooltip !== key) return null;
          const pct = (m.seconds / effectiveDuration) * 100;
          const tipStyle = pct < 30
            ? { left: `${pct}%`, transform: 'translateX(0)' }
            : pct > 70
              ? { left: `${pct}%`, transform: 'translateX(-100%)' }
              : { left: `${pct}%`, transform: 'translateX(-50%)' };
          return (
            <div
              key={`tip-${key}`}
              className="rapport-timeline-tooltip"
              style={tipStyle}
            >
              <strong>{m.step ? `Stap ${m.step}: ` : ''}{m.stepTitle}</strong>
              <span className={`rapport-timeline-tag rapport-timeline-tag--${m.type}`}>
                {m.type === 'sterk_punt' ? 'Sterk punt' : m.type === 'verbeterpunt' ? 'Verbeterpunt' : 'Notitie'}
              </span>
              <p>{m.context}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function RapportPage() {
  const { documentId } = useParams();
  const { currentUser, doorloopjes, globalSettings } = useDoorloopjes();
  const videoRef = useRef(null);
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoMime, setVideoMime] = useState(null);
  // Video's worden na de bewaartermijn automatisch verwijderd; het rapport blijft.
  const [videoVerwijderd, setVideoVerwijderd] = useState(false);
  const bewaartermijn = useVideoBewaartermijn();
  const alleenAudio = Boolean(analyse?.alleen_audio);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const pdfContentRef = useRef(null);
  const [openSteps, setOpenSteps] = useState({});
  const [openDoorloopjeSteps, setOpenDoorloopjeSteps] = useState({});

  const toggleStep = (stepNum) => {
    setOpenSteps(prev => ({ ...prev, [stepNum]: !prev[stepNum] }));
  };

  const toggleDoorloopjeStep = (stepNum) => {
    setOpenDoorloopjeSteps(prev => ({ ...prev, [stepNum]: !prev[stepNum] }));
  };

  const handleDownloadPDF = async () => {
    if (!analyse?.result || !pdfContentRef.current) return;
    setIsGeneratingPDF(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = pdfContentRef.current;

      // Dynamic import to reduce bundle size
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      let currentY = margin;

      // Render all small blocks individually so steps can break across pages
      const blocks = element.querySelectorAll('.pdf-rapport-piece');
      const imgWidth = pageWidth - (2 * margin);

      for (let i = 0; i < blocks.length; i++) {
        const canvas = await html2canvas(blocks[i], metLiveStijlen({
          scale: 1.5,
          useCORS: true,
          allowTaint: false,
          imageTimeout: 15000,
          backgroundColor: '#ffffff',
          logging: false,
        }));

        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Start new page if block doesn't fit (but always place the first block)
        if (currentY + imgHeight > pageHeight - margin && currentY > margin) {
          pdf.addPage();
          currentY = margin;
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 2;
      }

      const title = analyse.doorloopje?.title || 'Video Analyse';
      const filename = `Rapport - ${title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      pdf.save(filename);

      trackEvent({
        type: 'pdf_download',
        path: window.location.pathname,
        title: document.title,
        payload: { content_type: 'video_analyse', content_title: title },
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('De pdf maken lukte niet. Probeer het opnieuw.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Video time tracking
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    async function fetchAnalysis() {
      try {
        const jwt = localStorage.getItem('jwt');
        const res = await axios.get(
          `${STRAPI_URL}/api/video-analyses/${documentId}?populate[doorloopje][fields][0]=documentId&populate[doorloopje][fields][1]=title`,
          { headers: videoHeaders(jwt) }
        );
        const data = res.data.data;

        if (data.status !== 'done' || !data.result) {
          setError('Dit rapport is nog niet beschikbaar.');
        } else {
          setAnalyse(data);
          try {
            const videoRes = await axios.get(
              `${STRAPI_URL}/api/video-analyses/${documentId}/video-url`,
              { headers: videoHeaders(jwt) }
            );
            setVideoUrl(videoRes.data.data.url);
            setVideoMime(videoRes.data.data.mime_type);
            setVideoVerwijderd(Boolean(videoRes.data.data.verwijderd));
          } catch { }
          try {
            const notesRes = await axios.get(
              `${STRAPI_URL}/api/video-annotations?filters[video_analyse][documentId][$eq]=${documentId}&sort=timestamp_seconds:asc`,
              { headers: videoHeaders(jwt) }
            );
            setNotes(notesRes.data.data || []);
          } catch { }
        }
      } catch (err) {
        if (isVerificatieFout(err)) return meldVerificatieVerlopen();
        console.error('Report fetch error:', err);
        setError(
          err.response?.status === 403
            ? 'Je hebt geen toegang tot dit rapport.'
            : err.response?.status === 404
            ? 'Analyse niet gevonden.'
            : 'Het rapport laden lukte niet. Probeer het opnieuw.'
        );
      } finally {
        setLoading(false);
      }
    }
    fetchAnalysis();
  }, [documentId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDuration = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };
    if (video.readyState >= 1 && video.duration && isFinite(video.duration)) {
      setDuration(video.duration);
    }
    const onSeeked = () => setCurrentTime(video.currentTime);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadedmetadata', onDuration);
    video.addEventListener('durationchange', onDuration);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadedmetadata', onDuration);
      video.removeEventListener('durationchange', onDuration);
    };
  }, [videoUrl]);

  const seekTo = seconds => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    const jwt = localStorage.getItem('jwt');
    try {
      const res = await axios.post(
        `${STRAPI_URL}/api/video-annotations`,
        { data: { video_analyse: documentId, timestamp_seconds: currentTime, text: newNoteText.trim() } },
        { headers: videoHeaders(jwt) }
      );
      setNotes(prev => [...prev, res.data.data].sort((a, b) => a.timestamp_seconds - b.timestamp_seconds));
      setNewNoteText('');
    } catch (err) { console.error('Failed to add note:', err); }
  };

  const handleSaveEdit = async noteDocId => {
    if (!editText.trim()) return;
    const jwt = localStorage.getItem('jwt');
    try {
      const res = await axios.put(
        `${STRAPI_URL}/api/video-annotations/${noteDocId}`,
        { data: { text: editText.trim() } },
        { headers: videoHeaders(jwt) }
      );
      setNotes(prev => prev.map(n => (n.documentId === noteDocId ? res.data.data : n)));
      setEditingNoteId(null);
      setEditText('');
    } catch (err) { console.error('Failed to update note:', err); }
  };

  const handleDeleteNote = async noteDocId => {
    if (!window.confirm('Notitie verwijderen?')) return;
    const jwt = localStorage.getItem('jwt');
    try {
      await axios.delete(`${STRAPI_URL}/api/video-annotations/${noteDocId}`, { headers: videoHeaders(jwt) });
      setNotes(prev => prev.filter(n => n.documentId !== noteDocId));
    } catch (err) { console.error('Failed to delete note:', err); }
  };

  const videoBeschikbaarTot = (() => {
    // De bucket telt vanaf de upload; bij een record dat verder ging is dat de laatste poging.
    if (!analyse?.gestart_op || !bewaartermijn) return null;
    const d = new Date(analyse.gestart_op);
    d.setDate(d.getDate() + bewaartermijn.dagen);
    return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  })();

  if (!currentUser?.has_video_analysis) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="page-container video-analyse-page">
        <div className="page-introduction"><h1>Rapport</h1></div>
        <main className="video-analyse-content"><p>Rapport laden...</p></main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container video-analyse-page">
        <div className="page-introduction"><h1>Rapport</h1></div>
        <main className="video-analyse-content">
          <p className="field-error">{error}</p>
          <Link to="/video-analyse" className="back-link">Terug naar overzicht</Link>
        </main>
      </div>
    );
  }

  const { result, doorloopje } = analyse;
  const timelineMarkers = result.momenten
    ? result.momenten.map(m => ({
        seconds: timestampToSeconds(m.timestamp),
        label: m.timestamp,
        step: m.stap_nummer,
        stepTitle: result.stappen?.find(s => s.stap_nummer === m.stap_nummer)?.stap_titel || `Stap ${m.stap_nummer}`,
        type: m.type,
        context: m.beschrijving,
      }))
    : [];

  // Find doorloopje steps from context for showing original step descriptions
  const doorloopjeData = doorloopjes?.find(d => d.documentId === doorloopje?.documentId);

  return (
    <div className="page-container video-analyse-page">
      <div className="page-introduction">
        <nav className="breadcrumbs">
          <Link to="/video-analyse">Video Analyse</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Rapport</span>
        </nav>
        <div className="page-header-row">
          <h1>Rapport</h1>
          <div className="page-header-acties">
            <VideoPrivacyInfo />
            <button className="btn-download-rapport" onClick={handleDownloadPDF} disabled={isGeneratingPDF}>
              {isGeneratingPDF ? 'PDF genereren...' : 'Download PDF'}
            </button>
          </div>
        </div>
      </div>

      <main className="video-analyse-content">
        <div className="rapport-layout">
          {/* Video — constrained width */}
          {(videoUrl || videoVerwijderd) && (
            <div className="rapport-video-sidebar">
              {videoUrl ? (
                <>
                  {/* Bij "alleen geluid" is er geen video bewaard: dezelfde bediening, maar dan een geluidsspeler. */}
                  {alleenAudio ? (
                    <audio
                      ref={videoRef}
                      className="rapport-audio-player"
                      controls
                      preload="metadata"
                      src={videoUrl}
                      onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                      onDurationChange={(e) => {
                        if (e.target.duration && isFinite(e.target.duration)) setDuration(e.target.duration);
                      }}
                      onSeeked={(e) => setCurrentTime(e.target.currentTime)}
                    >
                      Je browser ondersteunt geen geluidsweergave.
                    </audio>
                  ) : (
                    <video
                      ref={videoRef}
                      className="rapport-video-player"
                      controls
                      preload="metadata"
                      onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                      onDurationChange={(e) => {
                        if (e.target.duration && isFinite(e.target.duration)) setDuration(e.target.duration);
                      }}
                      onSeeked={(e) => setCurrentTime(e.target.currentTime)}
                    >
                      <source src={videoUrl} type={videoMime || 'video/mp4'} />
                      Je browser ondersteunt geen video weergave.
                    </video>
                  )}
                  <TimelineBar
                    markers={timelineMarkers}
                    notes={notes}
                    duration={duration}
                    currentTime={currentTime}
                    onSeek={seekTo}
                  />
                  {videoBeschikbaarTot && (
                    <p className="rapport-video-bewaartermijn">
                      De {alleenAudio ? 'geluidsopname' : 'video'} wordt op {videoBeschikbaarTot} automatisch verwijderd. Het rapport en je notities blijven.
                    </p>
                  )}
                </>
              ) : (
                <div className="rapport-video-verwijderd">
                  <strong>{alleenAudio ? 'Opname verwijderd' : 'Video verwijderd'}</strong>
                  {bewaartermijn && (
                    <p>Opnames worden {bewaartermijn.woorden} na het uploaden automatisch verwijderd. Het rapport en je notities blijven beschikbaar.</p>
                  )}
                </div>
              )}

              {/* Notes — under video */}
              <div className="rapport-notes">
                <h3 className="rapport-section-label">Jouw notities</h3>
                <div className="rapport-notes-form">
                  <span className="rapport-notes-timestamp">{formatTime(currentTime)}</span>
                  <input
                    type="text"
                    value={newNoteText}
                    onChange={e => setNewNoteText(e.target.value)}
                    placeholder="Notitie toevoegen..."
                    className="rapport-notes-input"
                    onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                  />
                  <button onClick={handleAddNote} className="rapport-notes-add-btn" disabled={!newNoteText.trim()}>
                    Toevoegen
                  </button>
                </div>
                {notes.map(note => (
                  <div key={note.documentId} className="rapport-note-item">
                    <button className="rapport-timestamp rapport-note-ts-btn" onClick={() => seekTo(note.timestamp_seconds)}>
                      {formatTime(note.timestamp_seconds)}
                    </button>
                    {editingNoteId === note.documentId ? (
                      <div className="rapport-note-edit">
                        <input
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          className="rapport-notes-input"
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveEdit(note.documentId);
                            if (e.key === 'Escape') setEditingNoteId(null);
                          }}
                          autoFocus
                        />
                        <button onClick={() => handleSaveEdit(note.documentId)} className="rapport-notes-save-btn">Opslaan</button>
                        <button onClick={() => setEditingNoteId(null)} className="rapport-notes-cancel-btn">Annuleren</button>
                      </div>
                    ) : (
                      <span className="rapport-note-text">{note.text}</span>
                    )}
                    {editingNoteId !== note.documentId && (
                      <div className="rapport-note-actions">
                        <button onClick={() => { setEditingNoteId(note.documentId); setEditText(note.text); }} title="Bewerken">&#9998;</button>
                        <button onClick={() => handleDeleteNote(note.documentId)} title="Verwijderen">&times;</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          )}

          {/* Rapport content */}
          <div className="rapport-container">
            {doorloopje && (
              <h2 className="rapport-doorloopje-title">{doorloopje.title}</h2>
            )}

            {analyse?.alleen_audio && (
              <p className="rapport-video-bewaartermijn" style={{ marginBottom: '1rem' }}>
                De AI maakte deze analyse alleen met het geluid. De video is niet bewaard. Wat je alleen kunt zien, kon de AI niet beoordelen.
              </p>
            )}

            {/* Inleiding */}
            {result.inleiding && (
              <div className="rapport-inleiding">
                <p className="rapport-section-text">{result.inleiding}</p>
              </div>
            )}

            <VideoFeedbackKaart analyseId={documentId} />

            {/* Stappen — inklapbaar */}
            {result.stappen.map((stap) => {
              const isOpen = openSteps[stap.stap_nummer];
              const doorloopjeStep = doorloopjeData?.steps?.[stap.stap_nummer - 1];
              const isDoorloopjeStepOpen = openDoorloopjeSteps[stap.stap_nummer];

              return (
                <div className={`rapport-step rapport-step--collapsible${isOpen ? ' open' : ''}`} key={stap.stap_nummer}>
                  <button className="rapport-step-header" onClick={() => toggleStep(stap.stap_nummer)}>
                    <span className="rapport-step-number">{stap.stap_nummer}</span>
                    <span className="rapport-step-title-text">{toTitleCase(stap.stap_titel)}</span>
                    <span className="rapport-step-arrow">{isOpen ? '▾' : '▸'}</span>
                  </button>

                  {isOpen && (
                    <div className="rapport-step-body">
                      {/* Doorloopje step info — expandable */}
                      {doorloopjeStep && (
                        <div className="rapport-doorloopje-step-info">
                          <button
                            className="rapport-doorloopje-step-toggle"
                            onClick={() => toggleDoorloopjeStep(stap.stap_nummer)}
                          >
                            📖 Wat zegt het doorloopje over deze stap?
                            <span>{isDoorloopjeStepOpen ? '▾' : '▸'}</span>
                          </button>
                          {isDoorloopjeStepOpen && (
                            <div className="rapport-doorloopje-step-content">
                              {doorloopjeStep.description && (
                                <div className="rapport-doorloopje-step-desc">
                                  {/* Render blocks as simple text */}
                                  {doorloopjeStep.description.map?.((block, i) => (
                                    <p key={i}>
                                      {block.children?.map(c => c.text).join('') || ''}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="rapport-section">
                        <p className="rapport-section-label">Observatie</p>
                        <p className="rapport-section-text"><TimestampText text={stap.observatie} videoRef={videoRef} /></p>
                      </div>

                      <div className="rapport-section">
                        <p className="rapport-section-label">Sterke punten</p>
                        <ul className="rapport-strengths-list">
                          {stap.sterke_punten.map((punt, i) => (
                            <li key={i}><TimestampText text={punt} videoRef={videoRef} /></li>
                          ))}
                        </ul>
                      </div>

                      <div className="rapport-section">
                        <p className="rapport-section-label">Verbeterpunt</p>
                        <p className="rapport-section-text"><TimestampText text={stap.verbeterpunt} videoRef={videoRef} /></p>
                      </div>

                      {stap.actie && (
                        <div className="rapport-section rapport-section--actie">
                          <p className="rapport-section-label">💡 Actie voor je volgende les</p>
                          <p className="rapport-section-text"><TimestampText text={stap.actie} videoRef={videoRef} /></p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {result.jouw_vragen && (
              <div className="rapport-step">
                <h3 className="rapport-step-title">Jouw vragen</h3>
                <p className="rapport-section-text"><TimestampText text={result.jouw_vragen} videoRef={videoRef} /></p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Hidden PDF template — each .pdf-rapport-piece is an individually placeable block */}
      <div className={`pdf-export-container pdf-rapport ${isGeneratingPDF ? 'generating' : ''}`} ref={pdfContentRef}>
        {/* Header */}
        <div className="pdf-rapport-piece">
          <div className="pdf-header">
            <div className="pdf-title-block">
              <p className="pdf-theme">VIDEO ANALYSE RAPPORT</p>
              <h1 className="pdf-title">{doorloopje?.title || 'Video Analyse'}</h1>
              <p className="pdf-rapport-meta" data-eigen-gegevens>
                {currentUser?.fullName || currentUser?.username || ''}
                {analyse?.gestart_op && ` — ${new Date(analyse.gestart_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}`}
              </p>
            </div>
            {globalSettings?.logo?.url && (
              <img
                src={proxify(globalSettings.logo.url)}
                alt="Doorloopjes"
                className="pdf-logo"
                crossOrigin="anonymous"
              />
            )}
          </div>
        </div>

        {/* Inleiding */}
        {result?.inleiding && (
          <div className="pdf-rapport-piece">
            <div className="pdf-rapport-inleiding">
              <p>{stripTimestamps(result.inleiding)}</p>
            </div>
          </div>
        )}

        {/* Stappen — header and each block are separate pieces */}
        {result?.stappen?.map((stap, idx) => (
          <React.Fragment key={stap.stap_nummer}>
            {idx > 0 && (
              <div className="pdf-rapport-piece">
                <hr className="pdf-rapport-stap-divider" />
              </div>
            )}
            <div className="pdf-rapport-piece">
              <div className="pdf-rapport-stap-header">
                <span className="pdf-step-number">{stap.stap_nummer}</span>
                <h3 className="pdf-rapport-stap-title">{toTitleCase(stap.stap_titel)}</h3>
              </div>
            </div>

            <div className="pdf-rapport-piece">
              <div className="pdf-rapport-block-indent">
                <p className="pdf-rapport-label">Observatie</p>
                <p className="pdf-rapport-text">{stripTimestamps(stap.observatie)}</p>
              </div>
            </div>

            <div className="pdf-rapport-piece">
              <div className="pdf-rapport-block-indent">
                <p className="pdf-rapport-label">Sterke punten</p>
                <ul className="pdf-rapport-list">
                  {stap.sterke_punten.map((punt, i) => (
                    <li key={i}>{stripTimestamps(punt)}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pdf-rapport-piece">
              <div className="pdf-rapport-block-indent">
                <p className="pdf-rapport-label">Verbeterpunt</p>
                <p className="pdf-rapport-text">{stripTimestamps(stap.verbeterpunt)}</p>
              </div>
            </div>

            {stap.actie && (
              <div className="pdf-rapport-piece">
                <div className="pdf-rapport-block-indent pdf-rapport-actie">
                  <p className="pdf-rapport-label">💡 Actie voor je volgende les</p>
                  <p className="pdf-rapport-text">{stripTimestamps(stap.actie)}</p>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Jouw vragen */}
        {result?.jouw_vragen && (
          <div className="pdf-rapport-piece">
            <div className="pdf-rapport-block-indent">
              <p className="pdf-rapport-label">Jouw vragen</p>
              <p className="pdf-rapport-text">{stripTimestamps(result.jouw_vragen)}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pdf-rapport-piece">
          <div className="pdf-footer">
            <p>Gegenereerd via doorloopjes.nl</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RapportPage;
